<script>
  import { router } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import Card from '../../components/card.svelte'
  import StatGrid from '../../components/stat_grid.svelte'
  import StatTile from '../../components/stat_tile.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import DataTable from '../../components/data_table.svelte'
  import FilterDate from '../../components/filter_date.svelte'
  import LineChart from '../../components/charts/line_chart.svelte'
  import BarChart from '../../components/charts/bar_chart.svelte'

  let { metrics = null, filters = {}, rejection_category_labels = {}, pending_submissions_url = '' } = $props()

  // The inputs show the range the server applied (defaults filled in, a reversed range swapped).
  const startDate = $derived(filters.start_date || '')
  const endDate = $derived(filters.end_date || '')

  // Both keys always go, blank when cleared, so the server can tell a cleared start date
  // (30-day fallback) from a first visit (last 14 days).
  const applyFilters = (changes) => {
    const range = { start_date: startDate, end_date: endDate, ...changes }
    router.get('/quest-dashboard', { start_date: range.start_date || '', end_date: range.end_date || '' }, { preserveState: true, preserveScroll: true })
  }

  const totals = $derived(metrics?.totals ?? null)
  const templates = $derived(metrics?.templates ?? [])
  const rejectionBreakdown = $derived(metrics?.rejection_breakdown ?? [])
  const timeSeries = $derived(metrics?.time_series ?? { labels: [], submissions: [], approved: [], rejected: [] })

  const formatRate = (rate) => `${((rate ?? 0) * 100).toFixed(1)}%`

  const formatHours = (hours) => {
    if (!hours || hours <= 0) return '—'
    if (hours < 1) return `${Math.round(hours * 60)}m`
    return `${hours.toFixed(1)}h`
  }

  const trendDatasets = $derived([
    { label: 'Submissions', data: timeSeries.submissions, color: '#7000ff' },
    { label: 'Approved', data: timeSeries.approved, color: '#00b894' },
    { label: 'Rejected', data: timeSeries.rejected, color: '#d63031' }
  ])

  const rejectionLabels = $derived(
    rejectionBreakdown.map((entry) => rejection_category_labels[entry.category] ?? entry.category)
  )
  const rejectionDatasets = $derived([
    { label: 'Rejections', data: rejectionBreakdown.map((entry) => entry.count), color: '#d63031' }
  ])
</script>

<Layout>
  <PageHeader title="Quest Dashboard" subtitle="Submission volume, approval rates, and review throughput" />

  <FilterBar>
    {#snippet start()}
      <!-- Remount on every response: a cleared input whose applied date didn't change would stay blank. -->
      {#key filters}
        <FilterDate
          label="Start Date"
          value={startDate}
          onchange={(v) => applyFilters({ start_date: v })}
        />
        <FilterDate
          label="End Date"
          value={endDate}
          onchange={(v) => applyFilters({ end_date: v })}
        />
      {/key}
    {/snippet}
  </FilterBar>

  {#if !totals}
    <Card>
      <p class="text-base-content/60">No metrics available for this range.</p>
    </Card>
  {:else}
    <div class="space-y-6">
      <Card title="Submissions">
        <StatGrid>
          <StatTile label="Submissions" value={totals.submissions.toLocaleString()} />
          <StatTile label="Approval Rate" value={formatRate(totals.approval_rate)} />
          <StatTile
            label="Pending Review"
            value={totals.pending.toLocaleString()}
            highlight={totals.pending > 0}
            href={totals.pending > 0 ? pending_submissions_url : undefined}
          />
          <StatTile label="Active Questers" value={totals.active_questers.toLocaleString()} />
          <StatTile label="Avg Time to Review" value={formatHours(totals.avg_time_to_review_hours)} />
        </StatGrid>
      </Card>

      <Card title="Trends">
        <LineChart labels={timeSeries.labels} datasets={trendDatasets} height={320} />
      </Card>

      <Card title="Rejection Reasons">
        {#if rejectionBreakdown.length === 0}
          <p class="text-base-content/60">No rejections in this range.</p>
        {:else}
          <BarChart
            labels={rejectionLabels}
            datasets={rejectionDatasets}
            horizontal
            height={Math.max(200, rejectionBreakdown.length * 44)}
          />
        {/if}
      </Card>

      <Card title="Quests">
        <DataTable items={templates} emptyMessage="No quests were active in this range.">
          {#snippet header()}
            <tr>
              <th>Name</th>
              <th class="text-center">Quests</th>
              <th class="text-center">Submissions</th>
              <th class="text-center">Approved</th>
              <th class="text-center">Rejected</th>
              <th class="text-center">Approval Rate</th>
              <th class="text-center">Example Coverage</th>
              <th class="text-center">Actions</th>
            </tr>
          {/snippet}
          {#snippet row(template)}
            <tr>
              <td>
                <a href={template.url} class="link link-hover font-semibold">{template.name}</a>
                {#if !template.active}
                  <div class="text-xs text-base-content/50">Inactive</div>
                {/if}
              </td>
              <td class="text-center">{template.quests_count}</td>
              <td class="text-center">{template.submissions.toLocaleString()}</td>
              <td class="text-center">{template.approved.toLocaleString()}</td>
              <td class="text-center">{template.rejected.toLocaleString()}</td>
              <td class="text-center">{formatRate(template.approval_rate)}</td>
              <td class="text-center">
                {#if template.example_coverage === null}
                  —
                {:else if template.example_coverage < 1}
                  <span class="badge badge-warning">{formatRate(template.example_coverage)}</span>
                {:else}
                  {formatRate(template.example_coverage)}
                {/if}
              </td>
              <td class="text-center">
                <a href={template.submissions_url} class="link link-hover">View submissions</a>
              </td>
            </tr>
          {/snippet}
        </DataTable>
      </Card>
    </div>
  {/if}
</Layout>

